import { cn } from '@gramas/ui'
import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'

export type TabKey = 'today' | 'scan' | 'foods' | 'trends' | 'settings'

const TABS: ReadonlyArray<{ key: TabKey; label: string; icon: ReactNode }> = [
  {
    key: 'today',
    label: 'Šiandien',
    icon: (
      <path
        d="M4 5.5h12M4 10h12M4 14.5h7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    ),
  },
  {
    key: 'foods',
    label: 'Produktai',
    icon: (
      <>
        <circle cx="9" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.6" />
        <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </>
    ),
  },
  {
    key: 'scan',
    label: 'Skenuoti',
    icon: (
      <>
        <path
          d="M3 7V4.5A1.5 1.5 0 014.5 3H7M13 3h2.5A1.5 1.5 0 0117 4.5V7M17 13v2.5a1.5 1.5 0 01-1.5 1.5H13M7 17H4.5A1.5 1.5 0 013 15.5V13"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <path d="M3 10h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </>
    ),
  },
  {
    key: 'trends',
    label: 'Tendencijos',
    icon: (
      <path
        d="M3 14l4.5-5 3.5 3L17 6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ),
  },
  {
    key: 'settings',
    label: 'Nustatymai',
    icon: (
      <>
        <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.6" />
        <path
          d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M15.3 4.7l-1.4 1.4M6.1 13.9l-1.4 1.4"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </>
    ),
  },
]

/** Scan sits in the middle: it is the action the app exists for. */
const ORDER: TabKey[] = ['today', 'foods', 'scan', 'trends', 'settings']

export function TabBar({ value, onChange }: { value: TabKey; onChange: (tab: TabKey) => void }) {
  const nav = useRef<HTMLElement>(null)

  /**
   * Publish this bar's real height to the root element.
   *
   * The toast host is a portal on the body — it has to be, to clear the sheets
   * that raise most of the app's undo offers — which means it cannot sit above
   * the tab bar by being a sibling of it. It could hard-code a number instead,
   * and that number would be wrong on the first phone with a different text
   * size. Measuring costs one observer on an element that changes size about
   * never.
   */
  useEffect(() => {
    const element = nav.current
    if (element == null) return
    const publish = () => {
      document.documentElement.style.setProperty('--tabbar-h', `${element.offsetHeight}px`)
    }
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return (
    <nav
      ref={nav}
      className="flex shrink-0 items-stretch border-t border-line bg-surface pb-[env(safe-area-inset-bottom)]"
      aria-label="Pagrindinė navigacija"
    >
      {ORDER.map((key) => {
        const tab = TABS.find((t) => t.key === key)!
        const isScan = key === 'scan'
        const selected = value === key
        return (
          <button
            key={key}
            type="button"
            // The tour lights this button rather than opening the scanner: a
            // camera permission prompt in the middle of a walkthrough is not a
            // walkthrough any more.
            data-tour={isScan ? 'scan-tab' : undefined}
            aria-current={selected ? 'page' : undefined}
            onClick={() => onChange(key)}
            className={cn(
              'group flex flex-1 flex-col items-center justify-center gap-1 py-2 transition-colors duration-[140ms]',
              selected ? 'text-ink' : 'text-mute',
            )}
          >
            <span
              className={cn(
                'flex items-center justify-center rounded-[var(--radius-control)]',
                'transition-[background-color,color,transform] duration-[120ms] ease-[var(--ease-out-quick)] group-active:scale-[0.96]',
                isScan
                  ? cn('size-9', selected ? 'bg-accent text-accent-ink' : 'bg-raised text-ink')
                  : 'size-6',
              )}
            >
              <svg viewBox="0 0 20 20" className="size-5" fill="none" aria-hidden="true">
                {tab.icon}
              </svg>
            </span>
            <span className="text-[0.6875rem] leading-none">{tab.label}</span>
          </button>
        )
      })}
    </nav>
  )
}
